"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot@1.1.2";
import { VariantProps, cva } from "class-variance-authority@0.7.1";
import { PanelLeftIcon } from "lucide-react@0.487.0";
import { motion, type HTMLMotionProps } from "motion/react";

import { useIsMobile } from "./use-mobile";
import { useDashboardMotion } from "./motion";
import { cn } from "@utils";
import { Button } from "./button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "14rem";
const SIDEBAR_WIDTH_MOBILE = "14rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean | ((open: boolean) => boolean)) => void;
  openTransient: boolean;
  setOpenTransient: React.Dispatch<React.SetStateAction<boolean>>;
  railVisible: boolean;
  setRailVisible: React.Dispatch<React.SetStateAction<boolean>>;
  railToggleBehavior: "hide-rail" | "open-close";
  setRailToggleBehavior: React.Dispatch<React.SetStateAction<"hide-rail" | "open-close">>;
  openMobile: boolean;
  setOpenMobile: React.Dispatch<React.SetStateAction<boolean>>;
  mobileBehavior: "sheet" | "rail";
  setMobileBehavior: React.Dispatch<React.SetStateAction<"sheet" | "rail">>;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

function SidebarProvider({
  defaultOpen = false,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(true);
  const [mobileBehavior, setMobileBehavior] = React.useState<"sheet" | "rail">("sheet");
  const [railVisible, setRailVisible] = React.useState(true);
  const [railToggleBehavior, setRailToggleBehavior] = React.useState<"hide-rail" | "open-close">("hide-rail");

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const [openTransient, setOpenTransient] = React.useState(false);
  const open = openProp ?? _open;
  const openRef = React.useRef(open);
  React.useEffect(() => {
    openRef.current = open;
  }, [open]);
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(openRef.current) : value;
      openRef.current = openState;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp],
  );

  React.useEffect(() => {
    if (open || !railVisible || (isMobile && mobileBehavior === "sheet")) {
      setOpenTransient(false);
    }
  }, [isMobile, mobileBehavior, open, railVisible]);

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    if (isMobile && mobileBehavior === "sheet") {
      setOpenMobile((open) => !open);
      return;
    }

    if (mobileBehavior === "rail") {
      if (!isMobile) {
        const nextOpen = !(open || openTransient);
        setRailVisible(nextOpen);
        setOpen(nextOpen);
        setOpenTransient(false);
        return;
      }

      if (railToggleBehavior === "open-close") {
        const nextOpen = !(open || openTransient);
        setRailVisible(nextOpen);
        setOpen(nextOpen);
        setOpenTransient(false);
        return;
      }

      setOpen(false);
      setOpenTransient(false);
      setRailVisible((visible) => !visible);
      return;
    }

    setOpen(false);
    setOpenTransient((open) => !open);
  }, [isMobile, mobileBehavior, open, openTransient, railToggleBehavior, setOpen, setOpenMobile, setOpenTransient, setRailVisible]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open || openTransient ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      openTransient,
      setOpenTransient,
      railVisible,
      setRailVisible,
      railToggleBehavior,
      setRailToggleBehavior,
      isMobile,
      openMobile,
      setOpenMobile,
      mobileBehavior,
      setMobileBehavior,
      toggleSidebar,
    }),
    [state, open, setOpen, openTransient, setOpenTransient, railVisible, setRailVisible, railToggleBehavior, setRailToggleBehavior, isMobile, openMobile, setOpenMobile, mobileBehavior, setMobileBehavior, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  mobileBehavior = "sheet",
  railToggleBehavior = "hide-rail",
  className,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
  mobileBehavior?: "sheet" | "rail";
  railToggleBehavior?: "hide-rail" | "open-close";
}) {
  const { isMobile, state, open, openTransient, railVisible, openMobile, setOpenMobile, setOpenTransient, setMobileBehavior, setRailToggleBehavior } = useSidebar();
  const usesMobileRail = isMobile && mobileBehavior === "rail";
  const railHidden = mobileBehavior === "rail" && !railVisible;
  const canToggleTransient = usesMobileRail && collapsible === "icon" && !open && !railHidden;
  const keepsCollapsedGap = openTransient && collapsible === "icon" && !open;
  const collapsedMode =
    state === "collapsed" ? (usesMobileRail ? collapsible : "offcanvas") : "";
  const handleSidebarClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(event);

      const target = event.target;
      const clickedInteractiveElement =
        target instanceof Element &&
        Boolean(
          target.closest(
            "a, button, input, select, textarea, summary, [role='button'], [role='menuitem'], [data-sidebar-toggle-ignore='true']",
          ),
        );

      if (event.defaultPrevented || !canToggleTransient || clickedInteractiveElement) {
        return;
      }

      setOpenTransient((current) => !current);
    },
    [canToggleTransient, onClick, setOpenTransient],
  );
  const handleSidebarMouseLeave = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(event);

      if (!canToggleTransient || !openTransient) {
        return;
      }

      setOpenTransient(false);
    },
    [canToggleTransient, onMouseLeave, openTransient, setOpenTransient],
  );

  React.useEffect(() => {
    setMobileBehavior(mobileBehavior);
    return () => setMobileBehavior("sheet");
  }, [mobileBehavior, setMobileBehavior]);

  React.useEffect(() => {
    setRailToggleBehavior(railToggleBehavior);
    return () => setRailToggleBehavior("hide-rail");
  }, [railToggleBehavior, setRailToggleBehavior]);

  React.useEffect(() => {
    if (!openTransient || !canToggleTransient) {
      return;
    }

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      const clickedInsideSidebar =
        target instanceof Element &&
        Boolean(target.closest('[data-slot="sidebar-container"]'));

      if (clickedInsideSidebar) {
        return;
      }

      setOpenTransient(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
    };
  }, [canToggleTransient, openTransient, setOpenTransient]);

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile && mobileBehavior === "sheet") {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className={cn(
        "group peer text-sidebar-foreground",
        mobileBehavior === "rail" ? "block" : "hidden md:block",
      )}
      data-state={state}
      data-collapsible={collapsedMode}
      data-transient={openTransient ? "true" : "false"}
      data-rail-visible={railVisible ? "true" : "false"}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
          railHidden
            ? "w-0"
            : [
                "group-data-[collapsible=offcanvas]:w-0",
                variant === "floating" || variant === "inset"
                  ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
                  : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
              ],
          "group-data-[side=right]:rotate-180",
          !railHidden &&
            keepsCollapsedGap &&
            (variant === "floating" || variant === "inset"
              ? "w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
              : "w-(--sidebar-width-icon)"),
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed inset-y-0 z-50 h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear",
          mobileBehavior === "rail" ? "flex" : "hidden md:flex",
          side === "left"
            ? railHidden
              ? "left-[calc(var(--sidebar-width)*-1)]"
              : "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : railHidden
              ? "right-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className,
        )}
        onClick={handleSidebarClick}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn("size-7", className)}
      onClick={(event: any) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "bg-background relative flex w-full flex-1 flex-col",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className,
      )}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "text-sidebar-foreground/80 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: HTMLMotionProps<"ul">) {
  const dashboardMotion = useDashboardMotion();

  return (
    <motion.ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      variants={dashboardMotion.container}
      initial="hidden"
      animate="show"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: HTMLMotionProps<"li">) {
  const dashboardMotion = useDashboardMotion();

  return (
    <motion.li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      layout={!dashboardMotion.shouldReduceMotion}
      variants={dashboardMotion.compactItem}
      className={cn("group/menu-item relative", className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button relative flex w-full items-center gap-2 overflow-hidden rounded-md border border-transparent p-2 text-left text-sm text-sidebar-foreground outline-hidden ring-sidebar-ring transition-[background-color,border-color,color,box-shadow,width,height,padding] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:opacity-0 before:transition-opacity hover:border-[var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-60 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-60 data-[active=true]:border-[var(--sidebar-border)] data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:shadow-sm data-[active=true]:before:bg-primary data-[active=true]:before:opacity-100 data-[active=true]:before:shadow-[0_0_8px_var(--glow)] data-[active=true]:[&>svg]:text-primary data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "border-[var(--sidebar-border)] bg-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  disabled = false,
  tooltip,
  closeTransientOnClick = true,
  className,
  onClick,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  closeTransientOnClick?: boolean;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : "button";
  const { state, openTransient, setOpenTransient } = useSidebar();
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);

      if (openTransient && closeTransientOnClick && !event.defaultPrevented) {
        setOpenTransient(false);
      }
    },
    [closeTransientOnClick, onClick, openTransient, setOpenTransient],
  );

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      disabled={disabled}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      onClick={handleClick}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed"}
        {...tooltip}
      />
    </Tooltip>
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
};
